import ArticleIcon from '@mui/icons-material/Article';
import EmailIcon from '@mui/icons-material/Email';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import Tooltip from '@mui/material/Tooltip';
import { motion } from 'framer-motion';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';

import { useAppState } from '../../Context/AppStateContext';
import { theme } from '../../theme';

// Subtle glow animation
const glowEffect = keyframes`
  0% { box-shadow: 0 0 5px rgba(31, 207, 94, 0.3); }
  50% { box-shadow: 0 0 10px rgba(31, 207, 94, 0.5); }
  100% { box-shadow: 0 0 5px rgba(31, 207, 94, 0.3); }
`;

const pulse = keyframes`
  0% { opacity: 0.7; }
  50% { opacity: 1; }
  100% { opacity: 0.7; }
`;

const LinksContainer = styled.div`
  position: fixed;
  bottom: 70px;
  left: 20px; /* Move to left side of screen */
  display: flex;
  gap: 12px;

  @media (max-width: 600px) {
    left: 10px;
    flex-direction: column;
    gap: 8px;
  }
`;

const IconLink = styled(motion.a)`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(0, 20, 0, 0.7);
  border: 1px solid ${theme.colors.green};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.green};
  transition: all 0.2s ease;
  backdrop-filter: blur(5px);

  &:hover {
    background: rgba(10, 30, 10, 0.8);
    animation: ${glowEffect} 2s infinite ease-in-out;
    color: white;
  }

  @media (max-width: 600px) {
    width: 32px;
    height: 32px;

    svg {
      font-size: 16px;
    }
  }
`;

const Links: React.FC = () => {
  const { isOnMobile } = useAppState();
  const { t } = useTranslation();

  const links = [
    {
      id: 'linkedin',
      tooltip: t('contact.social.linkedin.label'),
      icon: <LinkedInIcon fontSize={isOnMobile ? 'small' : 'medium'} />,
      url: t('contact.social.linkedin.url'),
    },
    {
      id: 'email',
      tooltip: t('contact.social.email.label'),
      icon: <EmailIcon fontSize={isOnMobile ? 'small' : 'medium'} />,
      url: t('contact.social.email.url'),
    },
    {
      id: 'github',
      tooltip: t('contact.social.github.label'),
      icon: <GitHubIcon fontSize={isOnMobile ? 'small' : 'medium'} />,
      url: t('contact.social.github.url'),
    },
    {
      id: 'medium',
      tooltip: t('contact.social.medium.label'),
      icon: <ArticleIcon fontSize={isOnMobile ? 'small' : 'medium'} />,
      url: t('contact.social.medium.url'),
    },
  ];

  return (
    <LinksContainer>
      {links.map(link => (
        <Tooltip key={link.id} title={link.tooltip} arrow placement={isOnMobile ? 'left' : 'top'}>
          <IconLink
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            {link.icon}
          </IconLink>
        </Tooltip>
      ))}
    </LinksContainer>
  );
};

export default Links;
